/**
 * View + sheet commands (Phase 4 — docs/bim-plan/03-phases.md). Views are stored
 * SETTINGS (a plan view = level + cut overrides; a section = its line; a 3d view = a
 * saved camera) — never separate documents; the live editors re-derive linework from the
 * one model. Sheets hold viewports (view ref + paper-inch center + scale override) and a
 * titleBlock string map. Every command is one transact() = one undo step; `viewports`
 * and `overrides` are small plain values replaced ATOMICALLY (the Path2D convention).
 */
import type {
  CategoryId, ElementId, LevelId, Path2D, PhaseId, PlanViewType, Pt2, Pt3, SheetId, SheetRecord, ViewId, ViewRecord,
} from '../types'
import { newSheetId, newViewId } from '../ids'
import {
  getSheet, getView, listPhases, listSheets, maps, recordToYMap, resolveAllElements, resolveElement,
  transact, type ModelDoc,
} from '../doc'
import { effectivePhase, isFirstPhaseView, phaseStatus, phaseVisible } from '../phasing'
import { isLevelFreePlanType, isLiveOnlyView } from '../viewTypes'

// ---------- views ----------

/**
 * Right-handed section/elevation line (r8 — Matt's report: created views rendered
 * MIRRORED vs the model). The drawing's horizontal axis is a→b, so a→b must be the
 * VIEWER'S RIGHT vector — lookDir × up, i.e. [lookDir.y, -lookDir.x] — or the view
 * reads mirror-image (cutSection only ever sign-matched the DEPTH direction to
 * lookDir, never the u axis). Creation sites hand us either ordering (manual tool
 * click order, auto-elevation derivation, a grip drag pulling one end past the
 * other) — normalize by swapping a/b when wrong-handed.
 */
export function orientSectionLine(
  line: { a: Pt2; b: Pt2 },
  lookDir: Pt2,
): { a: Pt2; b: Pt2 } {
  const dx = line.b[0] - line.a[0], dy = line.b[1] - line.a[1]
  // d · right(lookDir) = d · [lookDir.y, -lookDir.x]
  return dx * lookDir[1] - dy * lookDir[0] >= 0 ? line : { a: line.b, b: line.a }
}

/**
 * One-shot repair for docs saved before the r8 orientation fix: re-orients every
 * stored section/elevation line, in ONE transaction, only when something is actually
 * wrong-handed. Returns the number of views fixed (0 = no transaction).
 */
export function normalizeViewOrientations(model: ModelDoc): number {
  const fixes: { id: ViewId; scope: ViewRecord['scope'] }[] = []
  for (const v of maps(model).views.values()) {
    const scope = (v as { get(k: string): unknown }).get('scope') as ViewRecord['scope'] | undefined
    if (!scope || (scope.kind !== 'section' && scope.kind !== 'elevation')) continue
    const oriented = orientSectionLine(scope.line, scope.lookDir)
    if (oriented !== scope.line) {
      fixes.push({ id: (v as { get(k: string): unknown }).get('id') as ViewId, scope: { ...scope, line: oriented } })
    }
  }
  if (fixes.length) {
    transact(model, () => {
      for (const f of fixes) maps(model).views.get(f.id)?.set('scope', f.scope)
    })
  }
  return fixes.length
}

export function createPlanView(
  model: ModelDoc,
  input: {
    /** required for story plan types; MUST be absent/ignored for the level-free
     *  types ('site' | 'roof' — docs/bim-plan/14 slice A) */
    name: string; levelId?: LevelId; cutHeightFt?: number; depthFt?: number; scale?: number
    /** level-free types only: absolute cut elevation (project ft); absent = derived
     *  above all content at read time */
    cutElevFt?: number
    /** multi-view stories (09 §6): the fixed built-in recipe token — absent = untyped */
    planType?: PlanViewType
    /** LIVE-VIEW HARDENING (2026-08-21): stamps this as the level-free category's
     *  `live` home view (see isLiveOnlyView) — ignored for story types, where
     *  liveness is already "no planType". Only `ensureCategoryLivePlan` should pass
     *  this; a normal "+ Plan view…" create never does. */
    live?: boolean
    /** §7 phase preset in the SAME transaction — a "Demo Floor Plan" is created whole */
    phase?: PhaseId
    phaseFilter?: NonNullable<ViewRecord['phaseFilter']>
    phaseGraphics?: NonNullable<ViewRecord['phaseGraphics']>
    /** round 11: an enlarged plan's region (story plans only) */
    region?: [number, number, number, number]
    /** PLANTING PLAN (2026-10-20): a site plan that shows plantings (ViewRecord.plantings) */
    plantings?: boolean
    /** ROOF FRAMING PLAN (2026-10-21): a roof plan that shows the roof framing (ViewRecord.roofFraming) */
    roofFraming?: boolean
  },
): ViewId {
  // bim-plan/14 slice A invariant: levelId present ⟺ story type, absent ⟺ level-free
  const levelFree = isLevelFreePlanType(input.planType)
  if (!levelFree && !input.levelId) throw new Error('createPlanView: a story plan view needs a levelId')
  const id = newViewId()
  const scope: ViewRecord['scope'] = {
    kind: 'plan',
    ...(levelFree ? {} : { levelId: input.levelId }),
    cutHeightFt: input.cutHeightFt ?? 4,
    ...(!levelFree && input.depthFt != null ? { depthFt: input.depthFt } : {}),
    // SUB-FLOOR (round 11): look 6' below the floor so the footings are in range
    ...(!levelFree && input.depthFt == null && (input.planType === 'subfloor' || input.planType === 'foundation') ? { depthFt: 6 } : {}),
    ...(levelFree && input.cutElevFt != null ? { cutElevFt: input.cutElevFt } : {}),
    ...(input.planType ? { planType: input.planType } : {}),
    ...(levelFree && input.live ? { live: true } : {}),
    ...(!levelFree && input.region ? { region: [
      Math.min(input.region[0], input.region[2]), Math.min(input.region[1], input.region[3]),
      Math.max(input.region[0], input.region[2]), Math.max(input.region[1], input.region[3]),
    ] as [number, number, number, number] } : {}),
  }
  transact(model, () => {
    maps(model).views.set(id, recordToYMap({
      id, name: input.name, scope, scale: input.scale ?? 0.25, overrides: {},
      ...(input.phase ? { phase: input.phase, phaseFilter: input.phaseFilter ?? 'asBuilt' } : {}),
      ...(input.phaseGraphics ? { phaseGraphics: input.phaseGraphics } : {}),
      ...(input.plantings && input.planType === 'site' ? { plantings: true } : {}),
      ...(input.roofFraming && input.planType === 'roof' ? { roofFraming: true } : {}),
    }))
  })
  return id
}

/** ROOF FRAMING PLAN (2026-10-21): turn a roof plan into a Roof Framing Plan or back (roof plans only) */
export function setRoofFramingPlan(model: ModelDoc, id: ViewId, on: boolean): boolean {
  const m = maps(model).views.get(id)
  if (!m) return false
  const scope = m.get('scope') as ViewRecord['scope'] | undefined
  if (!scope || scope.kind !== 'plan' || scope.planType !== 'roof') return false
  transact(model, () => { if (on) m.set('roofFraming', true); else m.delete('roofFraming') })
  return true
}

/** PLANTING PLAN (2026-10-20): turn a site plan into a Planting Plan or back (site plans only) */
export function setPlantingPlan(model: ModelDoc, id: ViewId, on: boolean): boolean {
  const m = maps(model).views.get(id)
  if (!m) return false
  const scope = m.get('scope') as ViewRecord['scope'] | undefined
  if (!scope || scope.kind !== 'plan' || scope.planType !== 'site') return false
  transact(model, () => { if (on) m.set('plantings', true); else m.delete('plantings') })
  return true
}

/**
 * VIEW PHASE INHERITANCE (2026-10-07, Jennie on Grabowski: an interior elevation
 * cut from the existing as-built plan came out on the New Construction phase, and
 * with it the existing kitchen/bath fixtures — all demolished in New Construction
 * — were gone): a section/elevation drawn FROM a plan carries that plan's stored
 * phase settings, the same §7 trio createPlanView already takes. Only a STORED
 * phase is copied — a plan with no phase of its own reads as the last phase, and
 * so does a view created with none, so absent stays absent.
 */
export interface VerticalPhaseInput {
  phase?: PhaseId
  phaseFilter?: NonNullable<ViewRecord['phaseFilter']>
  phaseGraphics?: NonNullable<ViewRecord['phaseGraphics']>
}
function verticalPhaseFields(input: VerticalPhaseInput): Partial<ViewRecord> {
  if (!input.phase) return {}
  return {
    phase: input.phase, phaseFilter: input.phaseFilter ?? 'asBuilt',
    ...(input.phaseGraphics ? { phaseGraphics: input.phaseGraphics } : {}),
  }
}
/** The stored phase trio of a source view (the plan the user drew from) — what
 *  every section/elevation creator takes. Empty when it has none. */
export function phaseInputFromView(
  v: Pick<ViewRecord, 'phase' | 'phaseFilter' | 'phaseGraphics'> | null | undefined,
): VerticalPhaseInput {
  if (!v?.phase) return {}
  return {
    phase: v.phase,
    ...(v.phaseFilter ? { phaseFilter: v.phaseFilter } : {}),
    ...(v.phaseGraphics ? { phaseGraphics: v.phaseGraphics } : {}),
  }
}

/** VERTICAL ANNOTATION round (docs/bim-plan/28 round 1, Matt's resolution): a new
 *  section's far clip — 10'-0" beyond the cut. Sections draw the cut only; the far
 *  clip bounds what the section's derived annotation (spot elevations) may reach. */
export const SECTION_FAR_CLIP_FT = 10

export function createSectionView(
  model: ModelDoc,
  input: { name: string; line: { a: Pt2; b: Pt2 }; lookDir: Pt2; depthFt?: number; scale?: number; levelId?: LevelId; sectionKind?: 'wall' } & VerticalPhaseInput,
): ViewId {
  const id = newViewId()
  const scope: ViewRecord['scope'] = {
    kind: 'section', line: orientSectionLine(input.line, input.lookDir), lookDir: input.lookDir, depthFt: input.depthFt ?? SECTION_FAR_CLIP_FT,
    ...(input.levelId ? { levelId: input.levelId } : {}),
    ...(input.sectionKind === 'wall' ? { sectionKind: 'wall' as const } : {}),
  }
  transact(model, () => {
    maps(model).views.set(id, recordToYMap({
      id, name: input.name, scope, scale: input.scale ?? 0.25, overrides: {},
      ...verticalPhaseFields(input),
    }))
  })
  return id
}

export function createElevationView(
  model: ModelDoc,
  input: { name: string; line: { a: Pt2; b: Pt2 }; lookDir: Pt2; depthFt?: number; scale?: number; levelId?: LevelId } & VerticalPhaseInput,
): ViewId {
  const id = newViewId()
  const scope: ViewRecord['scope'] = {
    kind: 'elevation', line: orientSectionLine(input.line, input.lookDir), lookDir: input.lookDir, depthFt: input.depthFt ?? 30,
    ...(input.levelId ? { levelId: input.levelId } : {}),
  }
  transact(model, () => {
    maps(model).views.set(id, recordToYMap({
      id, name: input.name, scope, scale: input.scale ?? 0.25, overrides: {},
      ...verticalPhaseFields(input),
    }))
  })
  return id
}

export function createCameraView(
  model: ModelDoc,
  input: { name: string; camera: { pos: Pt3; target: Pt3; ortho: boolean; fov?: number; twoPoint?: boolean; twoPointShift?: number }; scale?: number },
): ViewId {
  const id = newViewId()
  const scope: ViewRecord['scope'] = { kind: '3d', camera: input.camera }
  transact(model, () => {
    maps(model).views.set(id, recordToYMap({
      id, name: input.name, scope, scale: input.scale ?? 0.25, overrides: {},
    }))
  })
  return id
}

export function renameView(model: ModelDoc, id: ViewId, name: string): boolean {
  const m = maps(model).views.get(id)
  if (!m) return false
  // GL5b: a FIRM-library detail's name is its adoption key and firm identity —
  // read-only in the drawing flow (the TemplatesWorkspace curator renames the
  // firm record; models conform through review & apply)
  const scope = m.get('scope') as ViewRecord['scope'] | undefined
  if (scope?.kind === 'detail' && scope.firmId) return false
  // LIVE-VIEW HARDENING: a live surface's name is fixed (Levels…/the category itself
  // owns it) — never exposed in the Navigator's rename control, guarded here too
  if (scope && isLiveOnlyView({ scope })) return false
  transact(model, () => { m.set('name', name) })
  return true
}

/**
 * CHANGE PLAN TYPE round (Matt: "Right now I can rename a view after it's
 * created, but... I cannot change its type"): the missing counterpart to
 * createPlanView's type-at-creation-only path. Never crosses the level-free
 * boundary (bim-plan/14 slice A) — a story type (floor/dimension/finish/rcp/
 * asbuilt) can only become another story type, and a level-free type (site/
 * roof) only another level-free type, since crossing it would also mean the
 * scope's SHAPE changes (levelId present vs. absent, cutElevFt vs. not) —
 * that's a bigger, deliberate decision (pick a story, re-anchor the cut),
 * not a type swap, so it's refused here rather than guessed at.
 * `planType: undefined` clears back to an untyped plan (recipe-less, the
 * classic pre-09-§6 semantics) — the mirror of createPlanView's optional
 * `planType`. Only ever changes the ONE stored token; every renderer already
 * re-resolves the recipe live, so nothing else needs to migrate.
 */
export function setViewPlanType(model: ModelDoc, id: ViewId, planType: PlanViewType | undefined): boolean {
  const m = maps(model).views.get(id)
  if (!m) return false
  const scope = m.get('scope') as ViewRecord['scope'] | undefined
  if (!scope || scope.kind !== 'plan') return false
  // LIVE-VIEW HARDENING: a live surface's type is fixed by what it IS (a story's
  // implicit plan is untyped by definition; a category's live view is that category)
  if (isLiveOnlyView({ scope })) return false
  if (isLevelFreePlanType(scope.planType) !== isLevelFreePlanType(planType)) return false
  if (scope.planType === planType) return true
  transact(model, () => {
    const next = { ...scope } as Record<string, unknown>
    if (planType === undefined) delete next.planType
    else next.planType = planType
    m.set('scope', next)
  })
  return true
}

/** Replace scope fields within the SAME kind (a view never changes kind). */
export function updateViewScope(
  model: ModelDoc,
  id: ViewId,
  patch: Partial<{
    /** cutElevFt (bim-plan/14): level-free plans' absolute cut elevation — null
     *  writes through as "back to derived-above-content" (the depthFt convention:
     *  consumers' own `!= null` checks treat a stored null as the default) */
    cutHeightFt: number; depthFt: number; levelId: LevelId; cutElevFt: number | null
    /** SITE PLAN BUILDING FOOTPRINT: a site plan's cut-at-level (null = off) */
    cutLevel: { levelId: LevelId; aboveFt: number } | null
    /** ROOF PLAN VIEW DEPTH: a level-free plan's view range bottom (null = unlimited) */
    depthLevel: { levelId: LevelId; offsetFt: number } | null
    line: { a: Pt2; b: Pt2 }; lookDir: Pt2
    camera: { pos: Pt3; target: Pt3; ortho: boolean; fov?: number; twoPoint?: boolean; twoPointShift?: number }
  }>,
): boolean {
  const m = maps(model).views.get(id)
  if (!m) return false
  const scope = m.get('scope') as ViewRecord['scope'] | undefined
  if (!scope) return false
  const next = { ...scope } as Record<string, unknown>
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) continue
    // optional scope fields settable when absent: depthFt on plans, levelId on
    // vertical views (the interior-elevation storey — viewport-crop round),
    // cutElevFt on LEVEL-FREE plans only (a story plan anchors at its level)
    const optional = (k === 'depthFt' && scope.kind === 'plan') ||
      (k === 'levelId' && (scope.kind === 'section' || scope.kind === 'elevation')) ||
      (k === 'cutElevFt' && scope.kind === 'plan' && !scope.levelId) ||
      (k === 'cutLevel' && scope.kind === 'plan' && !scope.levelId && scope.planType === 'site') ||
      (k === 'depthLevel' && scope.kind === 'plan' && !scope.levelId)
    if (!(k in scope) && !optional) continue
    next[k] = v
  }
  // r8: line/lookDir edits (grip drags, the flip chip) can leave the line wrong-handed
  // — re-orient so the view never renders mirrored (see orientSectionLine)
  if ((scope.kind === 'section' || scope.kind === 'elevation') && next.line && next.lookDir) {
    next.line = orientSectionLine(next.line as { a: Pt2; b: Pt2 }, next.lookDir as Pt2)
  }
  transact(model, () => { m.set('scope', next) })
  return true
}

/**
 * CAD r8 (Matt: "never let the scale it's set to on the sheet be different to the
 * scale it's set to in its own open view — never let them differ. Same with all
 * saved drawings, actually"): the view's OWN scale is the single authoritative
 * value — every viewport placing this view, on EVERY sheet, updates to match in
 * the same transaction. There is no second stored scale to drift out of sync with
 * it; a sheet-side scale edit routes back through this same function (the
 * SheetsWorkspace per-viewport select calls setViewScale, never updateViewport's
 * own `scale` patch), so both directions funnel through one write.
 *
 * This supersedes the GL1 "a later view-scale change must not resize a placed
 * drawing" guarantee for the SCALE field specifically — an intentional scale edit
 * IS meant to resize what's on the sheet, which is the whole point of changing it.
 * anchorFt/cropWorldFt registration stability (WHERE a drawing sits, and what's
 * clipped) is untouched; only the size multiplier moves.
 */
export function setViewScale(model: ModelDoc, id: ViewId, scale: number): boolean {
  const m = maps(model).views.get(id)
  if (!m || !(scale > 0)) return false
  // GL5b: a FIRM-library detail's default placement scale is part of the standard
  const scope = m.get('scope') as ViewRecord['scope'] | undefined
  if (scope?.kind === 'detail' && scope.firmId) return false
  transact(model, () => {
    m.set('scale', scale)
    const { sheets } = maps(model)
    for (const sm of sheets.values()) {
      const vps = (sm.get('viewports') as SheetRecord['viewports'] | undefined) ?? []
      if (!vps.some((vp) => vp.viewId === id && vp.scale !== scale)) continue
      sm.set('viewports', vps.map((vp) => (vp.viewId === id ? { ...vp, scale } : vp)))
    }
  })
  return true
}

// ---------- r14: per-view element hides (Revit's "Hide in View > Elements") ----------

export function hideElementsInView(model: ModelDoc, id: ViewId, elementIds: ElementId[]): boolean {
  const m = maps(model).views.get(id)
  if (!m || !elementIds.length) return false
  const cur = (m.get('hiddenElements') as ElementId[] | undefined) ?? []
  const next = [...new Set([...cur, ...elementIds])]
  transact(model, () => { m.set('hiddenElements', next) })
  return true
}

export function unhideElementsInView(model: ModelDoc, id: ViewId, elementIds: ElementId[]): boolean {
  const m = maps(model).views.get(id)
  if (!m || !elementIds.length) return false
  const drop = new Set(elementIds)
  const cur = (m.get('hiddenElements') as ElementId[] | undefined) ?? []
  const next = cur.filter((e) => !drop.has(e))
  if (next.length === cur.length) return false
  transact(model, () => {
    if (next.length) m.set('hiddenElements', next)
    else m.delete('hiddenElements')
  })
  return true
}

/**
 * The LIVE plan's view identity (r14): every level has an implicit floor plan
 * (ArchiCAD's Story convention — the sheet picker already find-or-creates it).
 * Hide-in-view on the live plan stores against this record, so the hide applies to
 * the live surface AND every sheet viewport of that level's plan.
 *
 * Multi-view stories (09 §6): only an UNTYPED plan view is the level's implicit
 * plan — typed views (Floor/Dimension/Finish/RCP) are additional named documents
 * and must never be adopted as the live surface's record, or a live-plan hide
 * would silently land on, say, the Demo Floor Plan.
 */
export function ensureLevelPlanView(
  model: ModelDoc,
  levelId: LevelId,
  opts: { name?: string; cutHeightFt?: number } = {},
): ViewId {
  for (const vm of maps(model).views.values()) {
    const scope = vm.get('scope') as ViewRecord['scope'] | undefined
    if (scope?.kind === 'plan' && scope.levelId === levelId && !scope.planType) return vm.get('id') as ViewId
  }
  return createPlanView(model, {
    name: opts.name ?? 'Level Plan',
    levelId,
    cutHeightFt: opts.cutHeightFt ?? 4,
  })
}

/** level-free default scale (bim-plan/14 resolved Q11 — site plans default to
 *  1/8"=1'-0", a notch below the story plans' 1/4"). ROOF PLANS default to 1/4"
 *  (Matt, 2026-10-08: "Roof plans should default to 1/4 scale not 1/8 scale across
 *  the board"). The one table NewPlanViewModal.jsx and the live views both read. */
export function defaultLevelFreeScale(planType: string | undefined | null): number {
  return planType === 'roof' ? 0.25 : 0.125
}

/**
 * LIVE-VIEW HARDENING (2026-08-21 — Matt: "the Site Plan and Roof Plan groups
 * should each have their own live view, same as a story"): the level-free twin of
 * ensureLevelPlanView. Every project has exactly ONE live view per category,
 * find-or-created here; unlike a story's implicit plan it always carries the
 * category's own `planType` (site/roof plans are never truly untyped — the type
 * IS the category), distinguished from ordinary named site/roof views by
 * `scope.live: true` (isLiveOnlyView).
 */
export function ensureCategoryLivePlan(model: ModelDoc, category: 'site' | 'roof'): ViewId {
  for (const vm of maps(model).views.values()) {
    const scope = vm.get('scope') as ViewRecord['scope'] | undefined
    if (scope?.kind === 'plan' && !scope.levelId && scope.planType === category && scope.live) {
      return vm.get('id') as ViewId
    }
  }
  return createPlanView(model, {
    name: category === 'site' ? 'Site Plan' : 'Roof Plan',
    planType: category, live: true, scale: defaultLevelFreeScale(category),
  })
}

/**
 * Strips any viewport whose view has since become live-only (isLiveOnlyView) from
 * every sheet — the sheet-side half of the hardening migration. Returns the number
 * of viewports removed (0 = no transaction). A dangling `viewId` with no resolvable
 * view is left alone; that's a separate, pre-existing concern.
 */
export function removeLiveViewsFromSheets(model: ModelDoc): number {
  let removed = 0
  const patches: { id: SheetId; viewports: SheetRecord['viewports'] }[] = []
  for (const sm of maps(model).sheets.values()) {
    const viewports = (sm.get('viewports') as SheetRecord['viewports'] | undefined) ?? []
    const kept = viewports.filter((vp) => {
      const v = getView(model, vp.viewId)
      return !v || !isLiveOnlyView(v)
    })
    if (kept.length !== viewports.length) {
      removed += viewports.length - kept.length
      patches.push({ id: sm.get('id') as SheetId, viewports: kept })
    }
  }
  if (patches.length) {
    transact(model, () => {
      for (const p of patches) maps(model).sheets.get(p.id)?.set('viewports', p.viewports)
    })
  }
  return removed
}

/**
 * One-time hardening migration (2026-08-21): every project gets a permanent live
 * Site Plan and live Roof Plan — promoting whichever site/roof view was created
 * FIRST (pre-hardening, every level-free view was equally "the" one) if any exist,
 * else minting a fresh one — and then strips every sheet viewport that pointed at a
 * view which is now live-only (that promoted view included, if it happened to be on
 * a sheet already: "the live view can never be placed on a sheet" applies
 * retroactively, per Matt). Idempotent — a project that already has both live views
 * only pays the sheet sweep. Same one-time-migration convention as
 * normalizeViewOrientations/ensurePhases: called once per doc-open, under the edit
 * lock, in ONE transaction per category plus one for the sheet sweep.
 */
export function hardenLiveViews(model: ModelDoc): number {
  let changed = 0
  for (const category of ['site', 'roof'] as const) {
    let hasLive = false
    let firstExisting: ViewId | null = null
    for (const vm of maps(model).views.values()) {
      const scope = vm.get('scope') as ViewRecord['scope'] | undefined
      if (scope?.kind !== 'plan' || scope.levelId || scope.planType !== category) continue
      if (scope.live) { hasLive = true; break }
      if (!firstExisting) firstExisting = vm.get('id') as ViewId
    }
    if (hasLive) continue
    if (firstExisting) {
      transact(model, () => {
        const vm = maps(model).views.get(firstExisting as ViewId)
        const scope = vm?.get('scope') as ViewRecord['scope'] | undefined
        if (vm && scope) vm.set('scope', { ...scope, live: true })
      })
    } else {
      createPlanView(model, {
        name: category === 'site' ? 'Site Plan' : 'Roof Plan',
        planType: category, live: true, scale: defaultLevelFreeScale(category),
      })
    }
    changed++
  }
  changed += bumpLiveRoofScale(model)
  changed += removeLiveViewsFromSheets(model)
  return changed
}

/**
 * ROOF PLANS AT 1/4" (Matt, 2026-10-08): the live Roof Plan every model already has was
 * minted at the old 1/8" default. Move it to 1/4" ONCE per model (a settings flag, so a
 * later deliberate 1/8" is never overridden) and only if it still sits at the old
 * default. Safe for layouts: a live view can never be placed on a sheet. Named roof
 * views are left alone — they may be on sheets.
 */
const ROOF_SCALE_FLAG = 'roofLiveScaleQuarter'
export function bumpLiveRoofScale(model: ModelDoc): number {
  const settings = maps(model).settings
  if (settings.get(ROOF_SCALE_FLAG)) return 0
  let bumped = 0
  transact(model, () => {
    for (const vm of maps(model).views.values()) {
      const scope = vm.get('scope') as ViewRecord['scope'] | undefined
      if (scope?.kind === 'plan' && !scope.levelId && scope.planType === 'roof' && scope.live
        && Math.abs((vm.get('scale') as number) - 0.125) < 1e-9) {
        vm.set('scale', 0.25)
        bumped++
      }
    }
    settings.set(ROOF_SCALE_FLAG, true)
  })
  return bumped + 1
}

/**
 * Merge one category (or, for object/fixture, one FAMILY — FAMILY CATEGORIES
 * round, 2026-08-26: `overrides` widened to accept either key in the SAME map,
 * see types.ts's own note) override. A key set to `undefined` in the patch
 * REMOVES that key (back to default — undefined values are never stored; Yjs
 * can't encode them); an empty patch {} clears the whole key. The function body
 * was already 100% key-generic before this round — only the type annotation
 * widened.
 */
export function setViewOverride(
  model: ModelDoc,
  id: ViewId,
  category: CategoryId | string,
  patch: { visible?: boolean; halftone?: boolean },
): boolean {
  const m = maps(model).views.get(id)
  if (!m) return false
  const cur = (m.get('overrides') as ViewRecord['overrides'] | undefined) ?? {}
  const next: ViewRecord['overrides'] = { ...cur }
  const merged: Record<string, boolean> = {}
  for (const [k, v] of Object.entries(cur[category] ?? {})) {
    if (v !== undefined) merged[k] = v as boolean
  }
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) delete merged[k]
    else merged[k] = v
  }
  if (Object.keys(patch).length === 0 || Object.keys(merged).length === 0) {
    delete next[category]
  } else {
    next[category] = merged
  }
  transact(model, () => { m.set('overrides', next) })
  return true
}

/**
 * Toggle the derived annotation layers per view — Phase-5 tags/markers (07 Part 3) and
 * the levels-as-datums level lines. Same semantics as setViewOverride:
 * explicit-undefined REMOVES a key (back to default ON); when nothing remains, the
 * whole field clears (absent = every layer on).
 */
export function setViewAnnotations(
  model: ModelDoc,
  id: ViewId,
  patch: NonNullable<ViewRecord['annotations']>,
): boolean {
  const m = maps(model).views.get(id)
  if (!m) return false
  const cur = (m.get('annotations') as ViewRecord['annotations'] | undefined) ?? {}
  const merged: Record<string, boolean> = {}
  for (const [k, v] of Object.entries(cur)) if (v !== undefined) merged[k] = v as boolean
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) delete merged[k]
    else merged[k] = v
  }
  transact(model, () => {
    if (Object.keys(merged).length === 0) m.delete('annotations')
    else m.set('annotations', merged)
  })
  return true
}

/**
 * ELEVATION BACKDROP (2026-08-21): same merge-a-bag semantics as setViewOverride —
 * explicit-undefined REMOVES a key (back to absent/off), and an empty result clears
 * the whole field. Elevation-only in practice (the UI never offers it elsewhere),
 * not enforced here — same trust-the-caller convention as every other view command.
 */
export function setViewBackdrop(
  model: ModelDoc,
  id: ViewId,
  patch: NonNullable<ViewRecord['backdrop']>,
): boolean {
  const m = maps(model).views.get(id)
  if (!m) return false
  const cur = (m.get('backdrop') as ViewRecord['backdrop'] | undefined) ?? {}
  const merged: Record<string, boolean | number> = {}
  for (const [k, v] of Object.entries(cur)) if (v !== undefined) merged[k] = v as boolean | number
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) delete merged[k]
    else merged[k] = v
  }
  transact(model, () => {
    if (Object.keys(merged).length === 0) m.delete('backdrop')
    else m.set('backdrop', merged)
  })
  return true
}

/**
 * SOLAR STUDY round (2026-08-22): per-view sun override — the structural sibling of
 * setViewBackdrop (same merge-a-bag semantics: explicit-undefined removes a key,
 * empty result deletes the whole field, absent = the elevation's own artistic 45°
 * sun). Elevation-only in practice, trust-the-caller like every other view command.
 */
export function setViewSun(
  model: ModelDoc,
  id: ViewId,
  patch: NonNullable<ViewRecord['sun']>,
): boolean {
  const m = maps(model).views.get(id)
  if (!m) return false
  const cur = (m.get('sun') as ViewRecord['sun'] | undefined) ?? {}
  const merged: Record<string, boolean> = {}
  for (const [k, v] of Object.entries(cur)) if (v !== undefined) merged[k] = v as boolean
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) delete merged[k]
    else merged[k] = v
  }
  transact(model, () => {
    if (Object.keys(merged).length === 0) m.delete('sun')
    else m.set('sun', merged)
  })
  return true
}

/**
 * The drawing-time phase toggle (docs/bim-plan/09 §7): `phaseId: null` clears it
 * (phasing has no effect on this view — the zero-migration default). Setting a phase
 * for the first time on a view with no `phaseFilter` yet defaults it to 'asBuilt'.
 */
export function setViewPhase(model: ModelDoc, id: ViewId, phaseId: PhaseId | null): boolean {
  const m = maps(model).views.get(id)
  if (!m) return false
  transact(model, () => {
    if (phaseId === null) m.delete('phase')
    else {
      m.set('phase', phaseId)
      if (!m.get('phaseFilter')) m.set('phaseFilter', 'asBuilt')
    }
  })
  return true
}

export function setViewPhaseFilter(model: ModelDoc, id: ViewId, filter: NonNullable<ViewRecord['phaseFilter']>): boolean {
  const m = maps(model).views.get(id)
  if (!m) return false
  transact(model, () => { m.set('phaseFilter', filter) })
  return true
}

/** The graphic-style axis (Complete/plain vs. Existing+New/coded) — independent of phaseFilter. */
export function setViewPhaseGraphics(model: ModelDoc, id: ViewId, graphics: NonNullable<ViewRecord['phaseGraphics']>): boolean {
  const m = maps(model).views.get(id)
  if (!m) return false
  transact(model, () => { m.set('phaseGraphics', graphics) })
  return true
}

/**
 * STORY UNDERLAY (docs/MODELER.md): which reference story ghosts behind this plan
 * view — see the ViewRecord doc comment (absent defaults to 'below') and
 * viewTypes.ts#resolveUnderlayLevelId (the read-time resolver). Stores the setting
 * verbatim, including an explicit 'below' — round-tripping the user's actual pick is
 * simpler than special-casing the value that happens to match today's default.
 */
export function setViewUnderlay(model: ModelDoc, id: ViewId, story: NonNullable<ViewRecord['underlayStory']>): boolean {
  const m = maps(model).views.get(id)
  if (!m) return false
  transact(model, () => { m.set('underlayStory', story) })
  return true
}

/** REVIT UNDERLAY round: Look Down (default — the key is deleted) or Look Up. */
export function setViewUnderlayOrientation(model: ModelDoc, id: ViewId, orientation: 'down' | 'up'): boolean {
  const m = maps(model).views.get(id)
  if (!m) return false
  transact(model, () => {
    if (orientation === 'up') m.set('underlayOrientation', 'up')
    else m.delete('underlayOrientation')
  })
  return true
}

/**
 * CORE-ONLY PLAN round: toggle this plan view's "easy alignment mode" — see the
 * `coreOnly` doc comment on `ViewRecord`. Default-never-stored: turning it back off
 * deletes the key rather than writing `false`.
 */
export function setViewCoreOnly(model: ModelDoc, id: ViewId, coreOnly: boolean): boolean {
  const m = maps(model).views.get(id)
  if (!m) return false
  transact(model, () => {
    if (coreOnly) m.set('coreOnly', true)
    else m.delete('coreOnly')
  })
  return true
}

/**
 * TRUE NORTH round: a plan view's display orientation — 'true' rotates the
 * DISPLAY so true north points up (siteSettings.northRotationDeg, resolved at
 * read time); 'project' clears the key (default-never-stored, the standard
 * annotation-toggle discipline).
 */
export function setViewOrientation(model: ModelDoc, id: ViewId, orientation: 'project' | 'true'): boolean {
  const m = maps(model).views.get(id)
  if (!m) return false
  transact(model, () => {
    if (orientation === 'true') m.set('orientation', 'true')
    else m.delete('orientation')
  })
  return true
}

// ---------- the live 3D viewport's phase setting ----------
// Unlike plan/section, the always-on live 3D viewport has no backing ViewRecord (it's
// the ambient viewport, not a stored view — saved 3D CAMERA views exist separately for
// sheets). Its phase setting persists as its own `settings` key instead, the same
// pattern datums.ts uses for the site datum.

const LIVE3D_KEY = 'live3dPhase'

export interface Live3dPhase {
  phase: PhaseId | null
  phaseFilter: NonNullable<ViewRecord['phaseFilter']>
  phaseGraphics: NonNullable<ViewRecord['phaseGraphics']>
}

/** RESOLVED (phase consolidation round): the stored setting run through
 *  `effectivePhase` — no "off" state; an unset phase reads as the LAST phase, and a
 *  viewport that never stored anything reads Complete/plain (exactly the look
 *  "phasing off" used to give it). `phase` is null only when the model has no phases. */
export function getLive3dPhase(model: ModelDoc): Live3dPhase {
  const v = maps(model).settings.get(LIVE3D_KEY) as Partial<Live3dPhase> | undefined
  return effectivePhase(listPhases(model), v)
}

/** Patch-merge (stores resolved current values — a first patch pins the defaults). */
export function setLive3dPhase(model: ModelDoc, patch: Partial<Live3dPhase>): void {
  transact(model, () => {
    const cur = getLive3dPhase(model)
    maps(model).settings.set(LIVE3D_KEY, { ...cur, ...patch })
  })
}

// WALL FRAMING round: the live 3D viewport's FRAMING display state.
// DEPRECATED (FRAMING SESSION Phase 4, 2026-08-13): superseded by the
// SYSTEMS-VISIBILITY multi-check — a PERSONAL preference (localStorage
// `ajModelerSystems` in ModelerPage) driving sceneStore's chunk-class filter
// plus lazy per-system generation (DisplayState.systems), never this shared
// Yjs setting (which also full-regenerated every collaborator's geometry on
// each flip via the settings observer). Kept only so old models' stored key
// stays readable/ignored; no live call sites remain.
const LIVE3D_FRAMING_KEY = 'live3dFraming'
export type Live3dFraming = 'with' | 'only' | 'off'

export function getLive3dFraming(model: ModelDoc): Live3dFraming {
  const v = maps(model).settings.get(LIVE3D_FRAMING_KEY)
  return v === 'only' || v === 'off' ? v : 'with'
}

export function setLive3dFraming(model: ModelDoc, mode: Live3dFraming): void {
  transact(model, () => { maps(model).settings.set(LIVE3D_FRAMING_KEY, mode) })
}

/** Refuses while any sheet viewport still references the view, or while it's the
 *  live surface of a story/category — a live view is a permanent fixture (a story
 *  always has an implicit plan; a site/roof category always has its live plan),
 *  never something to delete-and-recreate. */
export function deleteView(model: ModelDoc, id: ViewId): boolean {
  if (listSheets(model).some((s) => s.viewports.some((vp) => vp.viewId === id))) return false
  const { views } = maps(model)
  if (!views.has(id)) return false
  const scope = views.get(id)?.get('scope') as ViewRecord['scope'] | undefined
  if (scope && isLiveOnlyView({ scope })) return false
  transact(model, () => {
    views.delete(id)
    // VIEW SCOPING round: the view's own drafting records render nowhere else —
    // delete them with it (sections had quietly leaked theirs since P6f)
    for (const [did, dm] of [...maps(model).drafting.entries()]) {
      const scope = dm.get('scope') as { kind: string; viewId?: ViewId } | undefined
      if (scope?.kind === 'view' && scope.viewId === id) maps(model).drafting.delete(did)
    }
  })
  return true
}

// ---------- sheets ----------

/**
 * LINKED SHEETS round: every new sheet is minted already tied to a real Drawing
 * Sheet the caller resolved via the picker (frontend `AddLinkedSheetModal`) —
 * there is no path to create a sheet with a made-up number/name anymore.
 * number/name/discipline come from that record, not from the caller's own
 * naming — pass exactly what's on the linked record.
 */
export function createSheet(
  model: ModelDoc,
  input: {
    airtableSheetId: string
    airtableTemplateId?: string
    sortKey?: string
    number: string
    name: string
    discipline?: string
    paper?: SheetRecord['paper']
    titleBlock?: Record<string, string>
  },
): SheetId {
  const id = newSheetId()
  transact(model, () => {
    maps(model).sheets.set(id, recordToYMap({
      id, airtableSheetId: input.airtableSheetId, number: input.number, name: input.name,
      ...(input.airtableTemplateId ? { airtableTemplateId: input.airtableTemplateId } : {}),
      ...(input.sortKey ? { sortKey: input.sortKey } : {}),
      ...(input.discipline ? { discipline: input.discipline } : {}),
      paper: input.paper ?? 'ARCH_D', viewports: [], titleBlock: input.titleBlock ?? {},
    }))
  })
  return id
}

/**
 * DRAFT SHEETS: a sheet made ahead of Cortex — typed number/name, a sheet type, no link. It
 * stays out of every release until "Add to a milestone" creates its Drawing Sheet and links it.
 */
export function createDraftSheet(
  model: ModelDoc,
  input: {
    number: string
    name: string
    airtableTemplateId?: string
    sortKey?: string
    discipline?: string
    paper?: SheetRecord['paper']
  },
): SheetId {
  const id = newSheetId()
  transact(model, () => {
    maps(model).sheets.set(id, recordToYMap({
      id, draft: true, number: input.number, name: input.name,
      ...(input.airtableTemplateId ? { airtableTemplateId: input.airtableTemplateId } : {}),
      ...(input.sortKey ? { sortKey: input.sortKey } : {}),
      ...(input.discipline ? { discipline: input.discipline } : {}),
      paper: input.paper ?? 'ARCH_D', viewports: [], titleBlock: {},
    }))
  })
  return id
}

/**
 * Stamp (or refresh) a sheet's link to its Drawing Sheet record — mirrors
 * number/name/discipline from it in the same transaction. Used by "Link unlinked…",
 * the set scaffold, and a draft sheet's "Add to a milestone" (which ends the draft).
 */
export function linkSheet(
  model: ModelDoc,
  id: SheetId,
  link: { airtableSheetId: string; number: string; name: string; discipline?: string; airtableTemplateId?: string; sortKey?: string },
): boolean {
  const m = maps(model).sheets.get(id)
  if (!m) return false
  transact(model, () => {
    m.set('airtableSheetId', link.airtableSheetId)
    m.delete('unlinkedFrom')
    m.delete('draft')
    if (link.airtableTemplateId) m.set('airtableTemplateId', link.airtableTemplateId)
    if (link.sortKey) m.set('sortKey', link.sortKey)
    m.set('number', link.number)
    m.set('name', link.name)
    if (link.discipline) m.set('discipline', link.discipline)
    else m.delete('discipline')
  })
  return true
}

/**
 * number/name are only independently editable here for a sheet that isn't
 * linked yet (a draft, or a pre-LINKED-SHEETS-round sheet pending reconciliation) — the
 * frontend only renders those two fields as editable when `!sheet.airtableSheetId`;
 * a linked sheet's identity comes from the record via `linkSheet` instead.
 */
export function updateSheet(
  model: ModelDoc,
  id: SheetId,
  patch: Partial<Pick<SheetRecord, 'number' | 'name' | 'paper'>>,
): boolean {
  const m = maps(model).sheets.get(id)
  if (!m) return false
  transact(model, () => {
    if (patch.number !== undefined) m.set('number', patch.number)
    if (patch.name !== undefined) m.set('name', patch.name)
    if (patch.paper !== undefined) m.set('paper', patch.paper)
  })
  return true
}

export function setTitleBlockField(model: ModelDoc, id: SheetId, key: string, value: string): boolean {
  const m = maps(model).sheets.get(id)
  if (!m) return false
  const cur = (m.get('titleBlock') as Record<string, string> | undefined) ?? {}
  const next = { ...cur }
  if (value === '') delete next[key]
  else next[key] = value
  transact(model, () => { m.set('titleBlock', next) })
  return true
}

/**
 * SHEET NOTES round (bim-plan/28 round 10): mirror a linked sheet's sheet TYPE (its Drawing Sheet
 * Template) and Cortex set order onto it — done on every editable open, so sheets linked before
 * round 8 learn them too. No-op when unchanged; returns whether anything changed.
 */
export function setSheetLinkMeta(model: ModelDoc, id: SheetId, meta: { airtableTemplateId?: string; sortKey?: string }): boolean {
  const m = maps(model).sheets.get(id)
  if (!m) return false
  const changes: [string, string][] = []
  if (meta.airtableTemplateId && m.get('airtableTemplateId') !== meta.airtableTemplateId) changes.push(['airtableTemplateId', meta.airtableTemplateId])
  if (meta.sortKey && m.get('sortKey') !== meta.sortKey) changes.push(['sortKey', meta.sortKey])
  if (!changes.length) return false
  transact(model, () => { for (const [k, v] of changes) m.set(k, v) })
  return true
}

/** ISSUANCE round: mirror the sheet's resolved issue fields (null clears). No-op when unchanged. */
export function setSheetIssuance(model: ModelDoc, id: SheetId, issuance: SheetRecord['issuance'] | null): boolean {
  const m = maps(model).sheets.get(id)
  if (!m) return false
  const cur = m.get('issuance') as SheetRecord['issuance'] | undefined
  if (JSON.stringify(cur ?? null) === JSON.stringify(issuance ?? null)) return false
  transact(model, () => {
    if (issuance) m.set('issuance', { ...issuance })
    else m.delete('issuance')
  })
  return true
}

/** SET SCAFFOLDING: place (or clear, null) a whole-sheet PDF in the drawing area */
export function setSheetPdfInsert(model: ModelDoc, id: SheetId, insert: { url: string } | null): boolean {
  const m = maps(model).sheets.get(id)
  if (!m) return false
  transact(model, () => {
    if (insert?.url) m.set('pdfInsert', { url: insert.url })
    else m.delete('pdfInsert')
  })
  return true
}

/** SET SCAFFOLDING: the plain-words notes of what the scaffold could not fill ([] clears) */
export function setSheetScaffoldNotes(model: ModelDoc, id: SheetId, notes: string[]): boolean {
  const m = maps(model).sheets.get(id)
  if (!m) return false
  transact(model, () => {
    if (notes.length) m.set('scaffoldNotes', [...notes])
    else m.delete('scaffoldNotes')
  })
  return true
}

/** AREA BREAKOUT: print (or stop printing) a level's area table on a sheet */
export function setSheetAreaTable(model: ModelDoc, id: SheetId, levelId: string, on: boolean): boolean {
  const m = maps(model).sheets.get(id)
  if (!m) return false
  const cur = (m.get('schedules') as SheetRecord['schedules'] | undefined) ?? {}
  const levels = new Set(cur.areaLevels || [])
  if (on) levels.add(levelId); else levels.delete(levelId)
  const next = { ...cur } as NonNullable<SheetRecord['schedules']>
  if (levels.size) next.areaLevels = [...levels]
  else delete next.areaLevels
  transact(model, () => {
    if (Object.keys(next).length) m.set('schedules', next)
    else m.delete('schedules')
  })
  return true
}

/**
 * OPENING SCHEDULES round: toggle the sheet's "include this schedule" setting
 * per family (`wall` added by the WALL ASSEMBLY SCHEDULE round). `false` deletes
 * the key and an all-off object deletes the whole field (the setViewAnnotations
 * sparse-store convention) — absent = off.
 */
export function setSheetSchedule(
  model: ModelDoc,
  id: SheetId,
  family: 'door' | 'window' | 'wall' | 'roof' | 'floor' | 'ceiling' | 'casework' | 'lighting' | 'equipment' | 'extLighting' | 'demolition' | 'lightingNotes',
  on: boolean,
): boolean {
  const m = maps(model).sheets.get(id)
  if (!m) return false
  const cur = (m.get('schedules') as SheetRecord['schedules'] | undefined) ?? {}
  const next = { ...cur }
  // DEMOLITION CALCULATIONS (round 11) print by default on a demolition plan sheet: the stored
  // value is the opt-OUT (false); every other family is opt-in (true)
  if (family === 'demolition') { if (on) delete next.demolition; else next.demolition = false }
  else if (on) next[family] = true
  else delete next[family]
  transact(model, () => {
    if (Object.keys(next).length) m.set('schedules', next)
    else m.delete('schedules')
  })
  return true
}

/**
 * Returns the new viewport's index. `centerIn` = paper inches from bottom-left.
 *
 * GL1: the SCALE is part of the viewport's fixed registration — stamped here from
 * the view when the caller passes none, never left to be read live off the view
 * (a later view-scale change must not resize a placed drawing). `anchorFt` and
 * `cropWorldFt` may be supplied at placement (the plan-registration default passes
 * an anchor); whatever is missing gets captured by the one-shot registration stamp
 * on the next sheet render.
 */
export function addViewport(
  model: ModelDoc,
  sheetId: SheetId,
  input: {
    viewId: ViewId; centerIn: Pt2; scale?: number
    anchorFt?: Pt2; cropWorldFt?: [number, number, number, number]
  },
): number {
  const m = maps(model).sheets.get(sheetId)
  const view = getView(model, input.viewId)
  if (!m || !view) return -1
  // LIVE-VIEW HARDENING (2026-08-21): the live surface of a story or a site/roof
  // category can never be placed on a sheet — only a NAMED typed view can. Place a
  // named plan view of that story/category instead ("+ Plan view…" in the Navigator).
  if (isLiveOnlyView(view)) return -1
  const cur = (m.get('viewports') as SheetRecord['viewports'] | undefined) ?? []
  // round 11: an enlarged plan arrives cropped to its region, its centre on the given point
  const region = view.scope.kind === 'plan' ? view.scope.region : undefined
  const cropWorldFt = input.cropWorldFt ?? (region ? [...region] as [number, number, number, number] : undefined)
  const anchorFt = input.anchorFt ?? (region ? [(region[0] + region[2]) / 2, (region[1] + region[3]) / 2] as Pt2 : undefined)
  const next = [...cur, {
    viewId: input.viewId, centerIn: input.centerIn,
    scale: input.scale ?? view.scale,
    ...(anchorFt ? { anchorFt } : {}),
    ...(cropWorldFt ? { cropWorldFt } : {}),
  }]
  // SET SCAFFOLDING: a placed drawing answers the "created empty" note
  transact(model, () => { m.set('viewports', next); m.delete('scaffoldNotes') })
  return next.length - 1
}

/**
 * `crop: null` clears the stored legacy crop delta; `cropWorldFt: null` clears the
 * stored world rect. Setting `cropWorldFt` also deletes any legacy `crop` delta —
 * the world rect supersedes it, and a stale delta lying underneath would silently
 * resurface if the rect were ever cleared.
 */
export function updateViewport(
  model: ModelDoc,
  sheetId: SheetId,
  index: number,
  patch: Partial<{
    centerIn: Pt2; scale: number
    crop: [number, number, number, number] | null
    cropWorldFt: [number, number, number, number] | null
    anchorFt: Pt2
  }>,
): boolean {
  const m = maps(model).sheets.get(sheetId)
  if (!m) return false
  const cur = (m.get('viewports') as SheetRecord['viewports'] | undefined) ?? []
  if (index < 0 || index >= cur.length) return false
  const next = cur.map((vp, i) => {
    if (i !== index) return vp
    const { crop, cropWorldFt, ...rest } = patch
    const merged = { ...vp, ...rest }
    if (crop === null) delete merged.crop
    else if (crop !== undefined) merged.crop = crop
    if (cropWorldFt === null) delete merged.cropWorldFt
    else if (cropWorldFt !== undefined) {
      merged.cropWorldFt = cropWorldFt
      delete merged.crop
    }
    return merged
  })
  transact(model, () => { m.set('viewports', next) })
  return true
}

/**
 * Stamp the FIXED REGISTRATION onto viewports that lack any part of it, in ONE
 * transaction — the one-shot migration that makes existing sheets stable (GL1,
 * generalizing the 2026-08-02 anchor stamp). Callers pass each viewport's CURRENT
 * resolved values — anchor = the crop centre, cropWorldFt = the resolved crop rect
 * (derived + any legacy delta), scale = the effective scale — so nothing visibly
 * moves: the placement, frame, and scale are simply pinned from now on instead of
 * being recomputed from the model every render.
 *
 * Per-FIELD: a viewport that already carries an anchor (the 2026-08 stamp) but no
 * cropWorldFt gains only the rect + scale; its anchor is never touched. Stamping
 * cropWorldFt absorbs and deletes the legacy `crop` delta. Idempotent — a fully
 * registered viewport is skipped, so this is safe to run on every sheet render.
 * Returns how many viewports were touched (0 = no transaction).
 */
export function stampViewportRegistrations(
  model: ModelDoc,
  sheetId: SheetId,
  entries: {
    index: number; anchorFt?: Pt2
    cropWorldFt?: [number, number, number, number]; scale?: number
  }[],
): number {
  const m = maps(model).sheets.get(sheetId)
  if (!m) return 0
  const cur = (m.get('viewports') as SheetRecord['viewports'] | undefined) ?? []
  let stamped = 0
  const next = cur.map((vp, i) => {
    const e = entries.find((p) => p.index === i)
    if (!e) return vp
    const out = { ...vp }
    let changed = false
    if (!out.anchorFt && e.anchorFt) { out.anchorFt = e.anchorFt; changed = true }
    if (!out.cropWorldFt && e.cropWorldFt) {
      out.cropWorldFt = e.cropWorldFt
      delete out.crop
      changed = true
    }
    if (out.scale == null && e.scale != null) { out.scale = e.scale; changed = true }
    if (changed) stamped++
    return changed ? out : vp
  })
  if (!stamped) return 0
  transact(model, () => { m.set('viewports', next) })
  return stamped
}

/** GL5c: designate (or undesignate) a sheet as the auto-details target. */
export function setSheetAutoDetails(model: ModelDoc, sheetId: SheetId, on: boolean): boolean {
  const m = maps(model).sheets.get(sheetId)
  if (!m) return false
  transact(model, () => {
    if (on) m.set('autoDetails', true)
    else m.delete('autoDetails')
  })
  return true
}

/**
 * GL5c: reconcile a sheet's AUTO viewports to the desired derived set, in ONE
 * transaction. Hand-placed viewports (no `auto` flag) are NEVER touched; auto
 * viewports whose view is still desired at the same placement keep their existing
 * entry (preserving the GL1 registration stamps), moved ones are re-created at the
 * new placement, stale ones are removed, and new ones append in the caller's order
 * (grid order — which is what makes the derived callout numbers read in grid order,
 * since viewSheetRef numbers by viewport index). Returns what changed; a no-diff
 * call makes no transaction at all.
 */
export function syncAutoViewports(
  model: ModelDoc,
  sheetId: SheetId,
  desired: { viewId: ViewId; centerIn: Pt2; scale: number }[],
): { added: number; removed: number; kept: number } {
  const m = maps(model).sheets.get(sheetId)
  const result = { added: 0, removed: 0, kept: 0 }
  if (!m) return result
  const cur = (m.get('viewports') as SheetRecord['viewports'] | undefined) ?? []
  const manual = cur.filter((vp) => !vp.auto)
  const curAuto = cur.filter((vp) => vp.auto)
  const autoByView = new Map(curAuto.map((vp) => [vp.viewId, vp]))
  const eq = (a: number, b: number) => Math.abs(a - b) < 1e-6
  const nextAuto: SheetRecord['viewports'] = []
  for (const d of desired) {
    const existing = autoByView.get(d.viewId)
    if (existing
      && eq(existing.centerIn[0], d.centerIn[0]) && eq(existing.centerIn[1], d.centerIn[1])
      && eq(existing.scale ?? d.scale, d.scale)) {
      nextAuto.push(existing)
      autoByView.delete(d.viewId)
      result.kept++
    } else {
      if (existing) autoByView.delete(d.viewId)   // moved — re-created fresh below
      nextAuto.push({ viewId: d.viewId, centerIn: d.centerIn, scale: d.scale, auto: true })
      result.added++
    }
  }
  result.removed = autoByView.size
  // order matters too — viewport index IS the derived callout number (viewSheetRef)
  const orderChanged = nextAuto.length !== curAuto.length || nextAuto.some((vp, i) => vp !== curAuto[i])
  if (!result.added && !result.removed && !orderChanged) return result   // byte-stable — no transaction
  const next = [...manual, ...nextAuto]
  transact(model, () => { m.set('viewports', next) })
  return result
}

export function removeViewport(model: ModelDoc, sheetId: SheetId, index: number): boolean {
  const m = maps(model).sheets.get(sheetId)
  if (!m) return false
  const cur = (m.get('viewports') as SheetRecord['viewports'] | undefined) ?? []
  if (index < 0 || index >= cur.length) return false
  const next = cur.filter((_vp, i) => i !== index)
  transact(model, () => { m.set('viewports', next) })
  return true
}

export function deleteSheet(model: ModelDoc, id: SheetId): boolean {
  const { sheets } = maps(model)
  if (!sheets.has(id)) return false
  transact(model, () => { sheets.delete(id) })
  return true
}

// ---------- derived viewport crops (viewport-crop round — docs/MODELER.md) ----------

/** breathing pad around a derived crop edge (ft) */
const CROP_PAD = 0.5
/** below the lowest floor on whole-building vertical views — shows the grade line */
const CROP_BELOW_GRADE = 1.5
/** storey height fallback when the model has a single level */
const DEFAULT_STORY_FT = 10

/**
 * The DERIVED crop rect for a view, in the view's own 2D space — (x, y) world feet
 * for plans, (u, z) for sections/elevations. Computed, never drawn (the round's
 * principle): the user only ever stores a DELTA on top of this.
 *
 * `contentBbox` is what the drawing CONTAINS — the cut's own bbox unioned with the
 * natural extent of its derived datum family (the caller owns that union; grids and
 * level lines are annotation, not geometry). Bounding the datums too is what keeps a
 * derived crop from slicing off the bubbles that carry their names.
 *
 * - plan: the level's content bbox (caller supplies it — the cut's bboxFt) + pad.
 * - vertical with `levelId` (interior elevation): the storey's floor-to-ceiling —
 *   the zero-setup ArchiCAD-style room bound.
 * - vertical without `levelId` (exterior/building section): bottom = lowest floor
 *   (crops below-grade strays), top = CONTENT top when known — the storey stack
 *   says nothing about ridge height, and a storey-derived top would cut roofs off
 *   (deliberate deviation from the plan text, recorded in docs/MODELER.md).
 * - horizontal on verticals: the line extent (cutSection already clips u to it).
 *
 * Returns null when underivable (3d views; a plan with no content bbox yet).
 */
export function deriveViewCrop(
  scope: ViewRecord['scope'],
  levels: { id: LevelId; elevationFt: number }[],
  contentBbox?: [number, number, number, number] | null,
): [number, number, number, number] | null {
  if (scope.kind === '3d') return null
  // GL5a details: like plans, the content IS the extent — the caller supplies the
  // drafting bbox (detailContentBbox); there is no model geometry to derive from
  if (scope.kind === 'plan' || scope.kind === 'detail') {
    if (!contentBbox) return null
    return [contentBbox[0] - CROP_PAD, contentBbox[1] - CROP_PAD, contentBbox[2] + CROP_PAD, contentBbox[3] + CROP_PAD]
  }
  const sorted = levels.slice().sort((a, b) => a.elevationFt - b.elevationFt)
  if (!sorted.length) return null
  const len = Math.hypot(scope.line.b[0] - scope.line.a[0], scope.line.b[1] - scope.line.a[1])
  /** floor-to-floor above level i; the top storey reuses the one below it */
  const storyAbove = (i: number): number =>
    i + 1 < sorted.length ? sorted[i + 1].elevationFt - sorted[i].elevationFt
    : sorted.length >= 2 ? sorted[sorted.length - 1].elevationFt - sorted[sorted.length - 2].elevationFt
    : DEFAULT_STORY_FT
  let z0: number, z1: number
  if (scope.levelId) {
    // interior elevation: its own storey, floor to ceiling
    const i = Math.max(0, sorted.findIndex((l) => l.id === scope.levelId))
    z0 = sorted[i].elevationFt - CROP_PAD
    z1 = sorted[i].elevationFt + storyAbove(i) + CROP_PAD
  } else {
    z0 = sorted[0].elevationFt - CROP_BELOW_GRADE
    // the storey stack says nothing about ridge height, so the CONTENT top wins
    // when known — a storey-derived top would decapitate every roof
    z1 = (contentBbox ? contentBbox[3] : sorted[sorted.length - 1].elevationFt + storyAbove(sorted.length - 1)) + CROP_PAD
    if (z1 <= z0) z1 = z0 + DEFAULT_STORY_FT
  }
  // horizontally the LINE governs (cutSection clips u to it), but never tighter than
  // the content — that slack is where a level datum's overshoot and bubble live
  const u0 = Math.min(-CROP_PAD, contentBbox ? contentBbox[0] - CROP_PAD : Infinity)
  const u1 = Math.max(len + CROP_PAD, contentBbox ? contentBbox[2] + CROP_PAD : -Infinity)
  return [u0, z0, u1, z1]
}

/**
 * The FINAL crop rect for a sheet viewport: derived + the stored user delta
 * (edge-wise). Degenerate results (user dragged an edge past its opposite) clamp
 * to a sliver instead of inverting. LEGACY (pre-GL1) — the path for viewports whose
 * registration hasn't been stamped yet; a registered viewport's crop is its stored
 * `cropWorldFt`, resolved by `resolveCropRect` below.
 */
export function viewportCropRect(
  derived: [number, number, number, number],
  crop?: [number, number, number, number],
): [number, number, number, number] {
  const r: [number, number, number, number] = crop
    ? [derived[0] + crop[0], derived[1] + crop[1], derived[2] + crop[2], derived[3] + crop[3]]
    : [...derived]
  if (r[2] - r[0] < 0.1) r[2] = r[0] + 0.1
  if (r[3] - r[1] < 0.1) r[3] = r[1] + 0.1
  return r
}

/**
 * GL1: the ONE crop resolution every consumer runs. A viewport with a stored
 * `cropWorldFt` gets it back VERBATIM (clamped against inversion) — no content bbox,
 * no datum bounds, no level math can move a registered frame. Only an un-migrated
 * viewport falls through to the legacy derived+delta path. `derived` may be null
 * (3d views; a plan whose cut hasn't arrived) — then a registered viewport still
 * resolves and a legacy one resolves to null.
 */
export function resolveCropRect(
  vp: {
    crop?: [number, number, number, number]
    cropWorldFt?: [number, number, number, number]
  } | null | undefined,
  derived: [number, number, number, number] | null,
): [number, number, number, number] | null {
  if (vp?.cropWorldFt) {
    const r: [number, number, number, number] = [...vp.cropWorldFt]
    if (r[2] - r[0] < 0.1) r[2] = r[0] + 0.1
    if (r[3] - r[1] < 0.1) r[3] = r[1] + 0.1
    return r
  }
  if (!derived) return null
  return viewportCropRect(derived, vp?.crop)
}

/**
 * GL1: a fully-registered viewport's paper rect — a PURE function of the stored
 * registration (centerIn, anchorFt, scale, cropWorldFt), nothing model-derived.
 * Mirrors the frontend placement math exactly (titleblock.js#viewportRectIn with
 * bbox = the crop rect, which is what cropLinework reports for a registered
 * viewport): x/y = anchor-relative offset from centerIn, paper inches y-UP from the
 * sheet's bottom-left. Null when any part of the registration is missing (legacy
 * viewport — the renderers fall back to content-derived placement until stamped).
 * This is the regression surface: the rect must be byte-identical across datum
 * re-benchmarks, level insertion, and arbitrary geometry edits.
 */
export function registeredPaperRect(
  vp: {
    centerIn: Pt2; scale?: number; anchorFt?: Pt2
    cropWorldFt?: [number, number, number, number]
  },
): { x: number; y: number; w: number; h: number } | null {
  if (!vp.anchorFt || !vp.cropWorldFt || vp.scale == null) return null
  const [x0, y0, x1, y1] = vp.cropWorldFt
  const s = vp.scale
  return {
    x: vp.centerIn[0] + (x0 - vp.anchorFt[0]) * s,
    y: vp.centerIn[1] + (y0 - vp.anchorFt[1]) * s,
    w: Math.max((x1 - x0) * s, 0.5),
    h: Math.max((y1 - y0) * s, 0.5),
  }
}

// ---------- GL1: project-level PLAN REGISTRATION ANCHOR (per scale) ----------
// One stored world point ↔ sheet point pair per plan scale, in the settings map
// (the site-datum pattern — undo-tracked, one per model). New plan viewports at a
// registered scale DEFAULT to it (anchorFt = world, centerIn = sheet), and existing
// ones re-align via alignedCenterIn — same-scale plans then land identically sheet
// to sheet. The sheet point is paper inches from the bottom-left; sheets of
// different paper sizes share it as-is (the firm runs one paper size per set).

const PLAN_REG_KEY = 'planRegistration'

export interface PlanRegistration {
  /** the world (plan view-space) point every same-scale plan pins */
  world: Pt2
  /** where that point lands on the sheet — paper inches from bottom-left */
  sheet: Pt2
}

const planRegScaleKey = (scale: number) => String(scale)

export function getPlanRegistration(model: ModelDoc, scale: number): PlanRegistration | null {
  const all = maps(model).settings.get(PLAN_REG_KEY) as Record<string, PlanRegistration> | undefined
  const v = all?.[planRegScaleKey(scale)]
  if (!v || !Array.isArray(v.world) || !Array.isArray(v.sheet)) return null
  return { world: [v.world[0], v.world[1]], sheet: [v.sheet[0], v.sheet[1]] }
}

/** One transact = one undo step; other scales' registrations are untouched. */
export function setPlanRegistration(model: ModelDoc, scale: number, reg: PlanRegistration): void {
  transact(model, () => {
    const s = maps(model).settings
    const all = { ...((s.get(PLAN_REG_KEY) as Record<string, PlanRegistration> | undefined) ?? {}) }
    all[planRegScaleKey(scale)] = {
      world: [reg.world[0], reg.world[1]],
      sheet: [reg.sheet[0], reg.sheet[1]],
    }
    s.set(PLAN_REG_KEY, all)
  })
}

/**
 * The centerIn that puts a viewport's own anchor where the registration says it
 * belongs: solve `sheet(reg.world) = reg.sheet` under the viewport's fixed
 * anchor↔centerIn mapping. Pure math — the "Align to registration" action.
 */
export function alignedCenterIn(reg: PlanRegistration, anchorFt: Pt2, scale: number): Pt2 {
  return [
    reg.sheet[0] + (anchorFt[0] - reg.world[0]) * scale,
    reg.sheet[1] + (anchorFt[1] - reg.world[1]) * scale,
  ]
}

// ---------- auto elevations (07 Part 1 — facade-derived, NEVER axis-derived) ----------

const DIR_TOL = (12 * Math.PI) / 180  // facade direction cluster tolerance (SANTIAGO FIX: 5° split a facade drawn a few degrees off into its own pair)
const CONNECT_TOL = 0.5               // plan endpoint proximity that joins two walls (ft)
const BUILDING_GAP = 6                // footprints closer than this are one building (ft)
const MIN_FACADE_LEN = 8              // clusters shorter than this are stub-wall noise
const LINE_PAD = 5                    // elevation line extends past the facade (ft)
const OFFSET_PAD = 10                 // elevation line distance outside the extent (ft)

interface WallGeo { id: ElementId; pts: Pt2[]; flipped: boolean }

function wallGeos(model: ModelDoc): WallGeo[] {
  const all: WallGeo[] = []
  const ext: WallGeo[] = []
  for (const el of resolveAllElements(model)) {
    if (el.category !== 'wall') continue
    const path = el.params.path as Path2D | null
    if (!path?.nodes || path.nodes.length < 2) continue
    const g = { id: el.id, pts: path.nodes.map((n) => n.pt), flipped: el.params.flipped === true }
    all.push(g)
    // SANTIAGO FIX (2026-10-01): EXTERIOR elevations come from EXTERIOR walls — an
    // angled interior partition used to mint its own redundant pair
    const fn = (el.typeParams?.function ?? el.type?.params?.function) as string | undefined
    if (fn === 'exterior') ext.push(g)
  }
  // a model with no exterior-typed walls yet keeps the old all-walls behavior
  return ext.length ? ext : all
}

/** plan distance from a point to a wall's polyline */
function distToWall(p: Pt2, w: WallGeo): number {
  let best = Infinity
  for (let i = 0; i < w.pts.length - 1; i++) {
    const a = w.pts[i], b = w.pts[i + 1]
    const dx = b[0] - a[0], dy = b[1] - a[1]
    const L2 = dx * dx + dy * dy
    const t = L2 > 0 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2)) : 0
    best = Math.min(best, Math.hypot(p[0] - (a[0] + dx * t), p[1] - (a[1] + dy * t)))
  }
  return best
}

/** Union-find over walls by PLAN endpoint proximity — detached buildings separate. */
function connectivityGroups(walls: WallGeo[]): WallGeo[][] {
  const parent = walls.map((_w, i) => i)
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i])))
  const union = (i: number, j: number) => { parent[find(i)] = find(j) }
  // SANTIAGO FIX (2026-10-01): a wall touching another ANYWHERE along it (a T, not just
  // end-to-end) is connected; and walls whose plan footprints overlap or sit within
  // BUILDING_GAP of each other are ONE building — stacked stories and unjoined runs of
  // one house used to read as dozens of "detached buildings", each with its own pairs
  for (let i = 0; i < walls.length; i++) {
    for (let j = i + 1; j < walls.length; j++) {
      const near = walls[i].pts.some((p) => distToWall(p, walls[j]) <= CONNECT_TOL)
        || walls[j].pts.some((q) => distToWall(q, walls[i]) <= CONNECT_TOL)
      if (near) union(i, j)
    }
  }
  const bboxOfGroup = (idx: number[]) => {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity
    for (const i of idx) for (const p of walls[i].pts) { x0 = Math.min(x0, p[0]); y0 = Math.min(y0, p[1]); x1 = Math.max(x1, p[0]); y1 = Math.max(y1, p[1]) }
    return [x0, y0, x1, y1]
  }
  for (let pass = 0; pass < 4; pass++) {
    const roots = new Map<number, number[]>()
    walls.forEach((_w, i) => { const r = find(i); const l = roots.get(r); if (l) l.push(i); else roots.set(r, [i]) })
    const list = [...roots.entries()].map(([r, idx]) => ({ r, bb: bboxOfGroup(idx) }))
    let merged = false
    for (let a = 0; a < list.length; a++) {
      for (let b = a + 1; b < list.length; b++) {
        const A = list[a].bb, B = list[b].bb
        const apart = A[0] > B[2] + BUILDING_GAP || B[0] > A[2] + BUILDING_GAP || A[1] > B[3] + BUILDING_GAP || B[1] > A[3] + BUILDING_GAP
        if (!apart && find(list[a].r) !== find(list[b].r)) { union(list[a].r, list[b].r); merged = true }
      }
    }
    if (!merged) break
  }
  const groups = new Map<number, WallGeo[]>()
  for (let i = 0; i < walls.length; i++) {
    const r = find(i)
    const g = groups.get(r)
    if (g) g.push(walls[i])
    else groups.set(r, [walls[i]])
  }
  return [...groups.values()]
}

/** Facade angle in [0, π) — direction mod 180° (a facade has two sides). */
function facadeAngle(a: Pt2, b: Pt2): number {
  let ang = Math.atan2(b[1] - a[1], b[0] - a[0])
  if (ang < 0) ang += Math.PI
  if (ang >= Math.PI - 1e-9) ang = 0
  return ang
}

function angleDist(a: number, b: number): number {
  const d = Math.abs(a - b)
  return Math.min(d, Math.PI - d)
}

/** Compass name of a plan direction (+Y = north): 8 sectors, 45° each. */
export function compassName(dir: Pt2): string {
  const names = ['East', 'Northeast', 'North', 'Northwest', 'West', 'Southwest', 'South', 'Southeast']
  const ang = Math.atan2(dir[1], dir[0])
  const sector = Math.round(ang / (Math.PI / 4))
  return names[((sector % 8) + 8) % 8]
}

export interface ElevationScopeInput extends VerticalPhaseInput {
  name: string
  line: { a: Pt2; b: Pt2 }
  lookDir: Pt2
  depthFt: number
  /** the storey this elevation belongs to (interior elevations only — the
   *  viewport-crop round's derived floor-to-ceiling vertical bound) */
  levelId?: LevelId
}

/**
 * Derive exterior elevation scopes from the model's WALL GEOMETRY (07 Part 1 — the
 * firm designs more than four-sided boxes, so auto-placement clusters actual facade
 * directions, never world axes): walls group by plan connectivity (detached buildings
 * elevate independently), each group's segments cluster by direction (±5°, mod 180°),
 * and every substantial cluster yields ONE elevation per side. A rectangular house
 * gives the classic four; a 45° wing adds its own pair; names come from the facade's
 * outward compass bearing with numeric suffixes on collision. Pure derivation — the
 * command below writes it in one transaction.
 */
export function deriveExteriorElevations(model: ModelDoc): ElevationScopeInput[] {
  const out: ElevationScopeInput[] = []
  const nameCounts = new Map<string, number>()
  for (const group of connectivityGroups(wallGeos(model))) {
    // direction clusters over the group's segments
    const clusters: { angle: number; len: number; segs: [Pt2, Pt2][] }[] = []
    for (const w of group) {
      for (let i = 0; i < w.pts.length - 1; i++) {
        const a = w.pts[i], b = w.pts[i + 1]
        const len = Math.hypot(b[0] - a[0], b[1] - a[1])
        if (len < 1e-6) continue
        const ang = facadeAngle(a, b)
        let best: (typeof clusters)[number] | null = null
        for (const c of clusters) {
          if (angleDist(c.angle, ang) <= DIR_TOL && (!best || angleDist(c.angle, ang) < angleDist(best.angle, ang))) best = c
        }
        if (best) {
          // length-weighted running mean keeps the cluster angle honest
          best.angle = best.angle + ((ang - best.angle) * len) / (best.len + len)
          best.len += len
          best.segs.push([a, b])
        } else {
          clusters.push({ angle: ang, len, segs: [[a, b]] })
        }
      }
    }
    const groupPts = group.flatMap((w) => w.pts)
    for (const c of clusters) {
      if (c.len < MIN_FACADE_LEN) continue   // stub-wall noise, skipped (logged in docs)
      const d: Pt2 = [Math.cos(c.angle), Math.sin(c.angle)]
      const n: Pt2 = [-d[1], d[0]]
      let umin = Infinity, umax = -Infinity, vmin = Infinity, vmax = -Infinity
      for (const p of groupPts) {
        const u = p[0] * d[0] + p[1] * d[1]
        const v = p[0] * n[0] + p[1] * n[1]
        if (u < umin) umin = u; if (u > umax) umax = u
        if (v < vmin) vmin = v; if (v > vmax) vmax = v
      }
      // BOTH sides per direction cluster, always — deterministic and wing-safe (a 45°
      // wing's facade sits nowhere near the group extent along its own normal, so any
      // reach-the-hull filter wrongly skips it). The cost: a lone diagonal interior
      // partition mints a redundant pair — the user deletes them, and "Elevate this
      // face" is the precision tool (07 Part 1). Same-direction interior partitions
      // merge into their facade's cluster and add nothing.
      for (const side of [1, -1] as const) {
        const v = side > 0 ? vmax + OFFSET_PAD : vmin - OFFSET_PAD
        const a: Pt2 = [d[0] * (umin - LINE_PAD) + n[0] * v, d[1] * (umin - LINE_PAD) + n[1] * v]
        const b: Pt2 = [d[0] * (umax + LINE_PAD) + n[0] * v, d[1] * (umax + LINE_PAD) + n[1] * v]
        const lookDir: Pt2 = [-n[0] * side, -n[1] * side]     // from outside, AT the building
        const facing: Pt2 = [n[0] * side, n[1] * side]        // the facade's outward normal
        const base = `${compassName(facing)} Elevation`
        const count = (nameCounts.get(base) ?? 0) + 1
        nameCounts.set(base, count)
        out.push({
          name: count === 1 ? base : `${base} ${count}`,
          // r8: right-handed a→b (viewer's right) — before this, one side of every
          // facade pair rendered mirrored
          line: orientSectionLine({ a, b }, lookDir),
          lookDir,
          depthFt: vmax - vmin + OFFSET_PAD + LINE_PAD,
        })
      }
    }
  }
  return out
}

/** Create every derived exterior elevation in ONE transaction (one undo step). */
export function createExteriorElevations(model: ModelDoc): ViewId[] {
  const scopes = deriveExteriorElevations(model)
  const ids: ViewId[] = []
  transact(model, () => {
    for (const s of scopes) {
      const id = newViewId()
      maps(model).views.set(id, recordToYMap({
        id, name: s.name,
        scope: { kind: 'elevation', line: s.line, lookDir: s.lookDir, depthFt: s.depthFt },
        scale: 0.25, overrides: {},
      }))
      ids.push(id)
    }
  })
  return ids
}

// ---------- interior elevations (r10 — Matt's kitchen test case) ----------

interface InteriorWallSeg {
  a: Pt2; b: Pt2; thicknessFt: number
}

function interiorWallSegs(model: ModelDoc, levelId?: string, phase?: VerticalPhaseInput): InteriorWallSeg[] {
  const out: InteriorWallSeg[] = []
  // VIEW PHASE INHERITANCE: only walls that EXIST in the source plan's phase can
  // be faced — on an existing as-built plan, a new partition in front of the real
  // wall used to stop the view (and its depth) short of the room it should show
  const phases = phase?.phase ? listPhases(model) : []
  const filter = phase?.phaseFilter ?? 'asBuilt'
  const isFirst = phases.length ? isFirstPhaseView(phases, phase!.phase) : false
  for (const el of resolveAllElements(model)) {
    if (el.category !== 'wall') continue
    if (levelId && el.levelId !== levelId) continue
    if (phases.length) {
      const st = phaseStatus(phases, el, phase!.phase)
      if (st !== null && !phaseVisible(filter, st, isFirst)) continue
    }
    const path = el.params.path as Path2D | null
    if (!path?.nodes || path.nodes.length < 2) continue
    const n = path.closed ? path.nodes.length : path.nodes.length - 1
    for (let i = 0; i < n; i++) {
      out.push({
        a: path.nodes[i].pt,
        b: path.nodes[(i + 1) % path.nodes.length].pt,
        thicknessFt: el.assemblyThicknessFt || 0.5,
      })
    }
  }
  return out
}

/** perpendicular foot of p on segment ab, clamped; null when degenerate */
function segFoot(p: Pt2, a: Pt2, b: Pt2): { pt: Pt2; dist: number; t: number } | null {
  const dx = b[0] - a[0], dy = b[1] - a[1]
  const len2 = dx * dx + dy * dy
  if (len2 < 1e-12) return null
  let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2
  t = Math.max(0, Math.min(1, t))
  const q: Pt2 = [a[0] + dx * t, a[1] + dy * t]
  return { pt: q, dist: Math.hypot(p[0] - q[0], p[1] - q[1]), t }
}

/**
 * Derive an INTERIOR elevation from a point in a room (r10 — the Revit placement
 * model Matt asked for): with no `dir`, the callout infers its orientation from the
 * NEAREST wall (re-aiming as the cursor moves); with a `dir` (the cardinal-checkbox
 * flow), it ray-casts that way and takes the first wall hit. The view comes out
 * PERFECTLY wall-orthogonal: the line runs parallel to the chosen wall segment
 * through the point, extends to the segment's full projected extent (+1ft pad),
 * looks at the wall, and clips just past its far face. Right-handed by
 * `orientSectionLine`, like every other creator. Pure derivation; null = no wall.
 */
export function deriveInteriorElevation(
  model: ModelDoc,
  at: Pt2,
  opts?: { dir?: Pt2; levelId?: string; phase?: VerticalPhaseInput },
): ElevationScopeInput | null {
  const segs = interiorWallSegs(model, opts?.levelId, opts?.phase)
  if (!segs.length) return null

  let chosen: { seg: InteriorWallSeg; dist: number } | null = null
  if (opts?.dir) {
    const dl = Math.hypot(opts.dir[0], opts.dir[1])
    if (dl < 1e-9) return null
    const rd: Pt2 = [opts.dir[0] / dl, opts.dir[1] / dl]
    for (const seg of segs) {
      // ray at + t·rd vs segment a + s·(b-a)
      const sx = seg.b[0] - seg.a[0], sy = seg.b[1] - seg.a[1]
      const denom = rd[0] * sy - rd[1] * sx
      if (Math.abs(denom) < 1e-9) continue
      const t = ((seg.a[0] - at[0]) * sy - (seg.a[1] - at[1]) * sx) / denom
      const s = ((seg.a[0] - at[0]) * rd[1] - (seg.a[1] - at[1]) * rd[0]) / denom
      if (t <= 1e-6 || s < -1e-6 || s > 1 + 1e-6) continue
      if (!chosen || t < chosen.dist) chosen = { seg, dist: t }
    }
  } else {
    for (const seg of segs) {
      const f = segFoot(at, seg.a, seg.b)
      if (f && (!chosen || f.dist < chosen.dist)) chosen = { seg, dist: f.dist }
    }
  }
  if (!chosen || chosen.dist < 1e-6) return null

  const { seg } = chosen
  const sdx = seg.b[0] - seg.a[0], sdy = seg.b[1] - seg.a[1]
  const slen = Math.hypot(sdx, sdy)
  if (slen < 1e-9) return null
  const d: Pt2 = [sdx / slen, sdy / slen]
  // look FROM the point AT the wall: the segment normal on the point's far side
  const n: Pt2 = [-d[1], d[0]]
  const side = (at[0] - seg.a[0]) * n[0] + (at[1] - seg.a[1]) * n[1] >= 0 ? 1 : -1
  const lookDir: Pt2 = [-n[0] * side, -n[1] * side]
  // extent: the wall segment's projection onto the view axis, padded
  const u0 = (seg.a[0] - at[0]) * d[0] + (seg.a[1] - at[1]) * d[1]
  const u1 = (seg.b[0] - at[0]) * d[0] + (seg.b[1] - at[1]) * d[1]
  const lo = Math.min(u0, u1) - 1, hi = Math.max(u0, u1) + 1
  const line = orientSectionLine({
    a: [at[0] + d[0] * lo, at[1] + d[1] * lo],
    b: [at[0] + d[0] * hi, at[1] + d[1] * hi],
  }, lookDir)
  const base = `${compassName(lookDir)} Interior Elevation`
  const taken = listViewNames(model)
  let name = base
  for (let i = 2; taken.has(name); i++) name = `${base} ${i}`
  return {
    name, line, lookDir, depthFt: chosen.dist + seg.thicknessFt + 1,
    // an interior elevation is ALWAYS a storey's elevation — recording which one
    // is what lets its sheet crop derive floor-to-ceiling with zero setup
    ...(opts?.levelId ? { levelId: opts.levelId as LevelId } : {}),
    ...(opts?.phase ?? {}),
  }
}

export function listViewNames(model: ModelDoc): Set<string> {
  const out = new Set<string>()
  maps(model).views.forEach((m) => out.add(m.get('name') as string))
  return out
}

/** Create the derived interior elevation (one undo step). Null = no wall to face. */
export function createInteriorElevation(
  model: ModelDoc,
  at: Pt2,
  opts?: { dir?: Pt2; levelId?: string; phase?: VerticalPhaseInput },
): ViewId | null {
  const scope = deriveInteriorElevation(model, at, opts)
  if (!scope) return null
  return createElevationView(model, scope)
}

/**
 * "Elevate this face" (07 Part 1): one click on a WALL derives the elevation of its
 * facade run — the contiguous chain of endpoint-connected, same-direction walls — from
 * its EXTERIOR side (left of the drawing direction, mirrored by `flipped` — the 2.6a
 * sidedness convention). Works at any orientation; the precision tool for complex
 * plans where clustering guesses wrong. Returns the created view id (null: not a wall
 * or degenerate geometry).
 */
export function elevateFace(model: ModelDoc, wallId: ElementId): ViewId | null {
  const el = resolveElement(model, wallId)
  if (!el || el.category !== 'wall') return null
  const walls = wallGeos(model)
  const self = walls.find((w) => w.id === wallId)
  if (!self || self.pts.length < 2) return null
  const ang = facadeAngle(self.pts[0], self.pts[self.pts.length - 1])
  // the facade run: endpoint-connected walls whose direction matches (±5°)
  const run: WallGeo[] = [self]
  let grew = true
  while (grew) {
    grew = false
    for (const w of walls) {
      if (run.includes(w)) continue
      if (angleDist(facadeAngle(w.pts[0], w.pts[w.pts.length - 1]), ang) > DIR_TOL) continue
      const touches = run.some((r) => r.pts.some((p) =>
        w.pts.some((q) => Math.hypot(p[0] - q[0], p[1] - q[1]) <= CONNECT_TOL)))
      if (touches) { run.push(w); grew = true }
    }
  }
  const a0 = self.pts[0], b0 = self.pts[self.pts.length - 1]
  const dLen = Math.hypot(b0[0] - a0[0], b0[1] - a0[1])
  if (dLen < 1e-6) return null
  const d: Pt2 = [(b0[0] - a0[0]) / dLen, (b0[1] - a0[1]) / dLen]
  // exterior = LEFT of the drawing direction; `flipped` mirrors it (06 §4 convention)
  const left: Pt2 = [-d[1], d[0]]
  const sign = self.flipped ? -1 : 1
  const ext: Pt2 = [left[0] * sign, left[1] * sign]
  const runPts = run.flatMap((w) => w.pts)
  let umin = Infinity, umax = -Infinity, vmin = Infinity, vmax = -Infinity
  for (const p of runPts) {
    const u = p[0] * d[0] + p[1] * d[1]
    const v = p[0] * ext[0] + p[1] * ext[1]
    if (u < umin) umin = u; if (u > umax) umax = u
    if (v < vmin) vmin = v; if (v > vmax) vmax = v
  }
  const v = vmax + OFFSET_PAD
  const a: Pt2 = [d[0] * (umin - LINE_PAD) + ext[0] * v, d[1] * (umin - LINE_PAD) + ext[1] * v]
  const b: Pt2 = [d[0] * (umax + LINE_PAD) + ext[0] * v, d[1] * (umax + LINE_PAD) + ext[1] * v]
  return createElevationView(model, {
    name: `${compassName(ext)} Elevation (face)`,
    line: { a, b },
    lookDir: [-ext[0], -ext[1]],
    depthFt: vmax - vmin + OFFSET_PAD + LINE_PAD,
  })
}

export { getView, getSheet }
